import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, CalendarClock, KanbanSquare, Receipt, AlertTriangle, Landmark, Users2, Download, Info, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

function sapi(env) {
  const h = { "X-Environment": env };
  return {
    get: (u, cfg = {}) => api.get(u, { ...cfg, headers: { ...(cfg.headers || {}), ...h } }),
    post: (u, b, cfg = {}) => api.post(u, b, { ...cfg, headers: { ...(cfg.headers || {}), ...h } }),
  };
}
function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white rounded-xl border border-border p-4 ${className}`} {...rest}>{children}</div>;
}
function Kpi({ label, value, tone = "", tid }) {
  return (
    <div className={`rounded-lg border border-border px-3 py-2.5 ${tone}`} data-testid={`kpi-${tid}`}>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}
const NAVY = "hsl(217 63% 15%)";
const SUBS = [
  ["portfolio", "المحفظة", "Portfolio", LayoutDashboard],
  ["deadlines", "المواعيد", "Deadlines", CalendarClock],
  ["pipeline", "خط الأنابيب", "Pipeline", KanbanSquare],
  ["filing", "التقديم والسداد", "Filing & Payment", Receipt],
  ["exceptions", "الاستثناءات", "Exceptions", AlertTriangle],
  ["zatca", "ملفات الهيئة", "ZATCA", Landmark],
  ["workload", "عبء العمل", "Workload", Users2],
];
const PORTFOLIO_ROLES = ["platform_admin", "tax_admin", "tax_manager", "management", "auditor"];

export default function CommandCenter({ env, user, L }) {
  const [sub, setSub] = useState("portfolio");
  const [kpis, setKpis] = useState([]);
  const [showGov, setShowGov] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [packages, setPackages] = useState([]);
  const [genBusy, setGenBusy] = useState(false);
  const s = sapi(env);
  const canReport = PORTFOLIO_ROLES.includes(user?.role);
  const canGenReport = ["platform_admin", "tax_admin", "tax_manager", "management"].includes(user?.role);
  useEffect(() => { s.get("/dashboard/kpi-catalogue").then(({ data }) => setKpis(data.kpis)).catch(() => {}); }, [env]); // eslint-disable-line
  const loadReports = useCallback(() => {
    if (!canReport) return;
    s.get("/dashboard/report-packages").then(({ data }) => setPackages(data.packages || [])).catch(() => {});
  }, [env]); // eslint-disable-line
  useEffect(() => { if (showReport) loadReports(); }, [showReport, loadReports]);
  const genReport = () => {
    setGenBusy(true);
    s.post("/dashboard/report-packages?lang=ar").then(({ data }) => {
      toast.success(L(`تم إنشاء تقرير الحالة (الإصدار ${data.version})`, `Management status report created (v${data.version})`));
      loadReports();
    }).catch((e) => {
      const st = e?.response?.status;
      if (st === 401 || st === 403) toast.error(L("لا تملك صلاحية إنشاء التقرير", "You are not authorized to generate the report"));
      else toast.error(L("تعذّر إنشاء التقرير، حاول لاحقاً", "Report could not be generated, please try again later"));
    }).finally(() => setGenBusy(false));
  };
  const dlReport = (id) => s.get(`/dashboard/report-packages/${id}/pdf`, { responseType: "blob" }).then((res) => {
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = `${id}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  }).catch((e) => {
    const st = e?.response?.status;
    if (st === 401 || st === 403) toast.error(L("لا تملك صلاحية تنزيل التقرير", "You are not authorized to download the report"));
    else toast.error(L("تعذّر تنزيل التقرير", "Report could not be downloaded"));
  });
  const canWorkload = PORTFOLIO_ROLES.includes(user?.role) || user?.role === "supervisor";
  const dl = async (panel) => {
    try {
      const res = await s.get(`/dashboard/export.csv?panel=${panel}`, { responseType: "blob" });
      const text = await res.data.text();
      const rows = text.split(/\r?\n/).filter((ln) => ln.trim() && !ln.startsWith("#"));
      if (rows.length <= 1) { toast.info(L("لا توجد بيانات للتصدير", "No data to export")); return; }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `${panel}_${env}.csv`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) toast.error(L("لا تملك صلاحية تصدير هذه البيانات", "You are not authorized to export this data"));
      else toast.error(L("تعذّر إكمال التصدير، حاول لاحقاً", "Export could not be completed, please try again later"));
    }
  };
  return (
    <div className="space-y-4" data-testid="command-center">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 flex-wrap">
          {SUBS.filter(([k]) => k !== "workload" || canWorkload).map(([k, ar, en, Icon]) => (
            <button key={k} data-testid={`cc-sub-${k}`} onClick={() => setSub(k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${sub === k ? "bg-[hsl(217_63%_15%)] text-white border-transparent" : "bg-white text-foreground/70 border-border hover:bg-secondary"}`}>
              <Icon className="h-3.5 w-3.5" /> {L(ar, en)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {canReport && (
            <button data-testid="cc-report-toggle" onClick={() => setShowReport((v) => !v)} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
              <FileText className="h-3.5 w-3.5" /> {L("تقرير الحالة الإدارية", "Management status report")}
            </button>
          )}
          <button data-testid="cc-governance-toggle" onClick={() => setShowGov((v) => !v)} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
            <Info className="h-3.5 w-3.5" /> {L("حوكمة المؤشرات", "KPI governance")}
          </button>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground" data-testid="cc-readonly-banner">
        {L("لقطة إدارية للقراءة فقط — ليست وثيقة اعتماد ضريبي أو تقديم.", "Read-only management snapshot — not a tax approval or filing document.")}
      </div>
      {showReport && canReport && (
        <Card data-testid="cc-report-packages">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">{L("تقارير الحالة الإدارية الموقّعة (نسخ محفوظة)", "Signed Management Status reports (immutable versions)")}</div>
            {canGenReport && (
              <button data-testid="cc-report-generate" disabled={genBusy} onClick={genReport}
                className="inline-flex items-center gap-1 text-[11px] rounded border border-border px-2 py-1 hover:bg-secondary disabled:opacity-50">
                <FileText className="h-3 w-3" /> {genBusy ? L("جارٍ الإنشاء…", "Generating…") : L("إنشاء تقرير PDF", "Generate PDF")}
              </button>
            )}
          </div>
          <div className="space-y-1" data-testid="cc-report-list">
            {packages.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5" data-testid={`cc-report-${p.id}`}>
                <span className="font-mono">{p.id}</span>
                <span className="bg-secondary px-1.5 py-0.5 rounded">v{p.version}</span>
                <span className="flex-1 text-muted-foreground truncate">{(p.generated_at || "").slice(0, 19)} · {p.generated_by}</span>
                <span className="font-mono text-[9px] text-muted-foreground">{(p.verification_hash || "").slice(0, 12)}…</span>
                <button data-testid={`cc-report-dl-${p.id}`} onClick={() => dlReport(p.id)} className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 hover:bg-secondary"><Download className="h-3 w-3" />PDF</button>
              </div>
            ))}
          </div>
          {!packages.length && <div className="text-[11px] text-muted-foreground" data-testid="cc-report-empty">{L("لا توجد تقارير بعد.", "No reports yet.")}</div>}
        </Card>
      )}
      {showGov && (
        <Card data-testid="cc-governance">
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("تعريفات المؤشرات ومصادرها", "KPI definitions & sources")}</div>
          <div className="overflow-x-auto"><table className="w-full text-[11px]"><thead><tr className="text-right text-muted-foreground">
            <th className="py-1 pe-3">KPI</th><th className="pe-3">{L("المصدر", "Source")}</th><th className="pe-3">{L("الحساب", "Calc")}</th><th className="pe-3">{L("النطاق", "Scope")}</th><th>{L("التنقيب", "Drill")}</th></tr></thead>
            <tbody>{kpis.map((k) => (<tr key={k.id} className="border-t border-border"><td className="py-1 pe-3 font-semibold">{L(k.ar, k.en)}</td><td className="pe-3 font-mono text-[10px]">{k.source}</td><td className="pe-3">{k.calc}</td><td className="pe-3">{k.scope}</td><td>{k.drill}</td></tr>))}</tbody>
          </table></div>
        </Card>
      )}
      {sub === "portfolio" && <Portfolio s={s} L={L} dl={dl} />}
      {sub === "deadlines" && <Deadlines s={s} L={L} dl={dl} />}
      {sub === "pipeline" && <Pipeline s={s} L={L} dl={dl} />}
      {sub === "filing" && <Filing s={s} L={L} />}
      {sub === "exceptions" && <Exceptions s={s} L={L} />}
      {sub === "zatca" && <Zatca s={s} L={L} dl={dl} />}
      {sub === "workload" && canWorkload && <Workload s={s} L={L} dl={dl} />}
    </div>
  );
}

function useData(s, url) {
  const [d, setD] = useState(null);
  const load = useCallback(() => s.get(url).then(({ data }) => setD(data)).catch(() => setD(null)), [url]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  return d;
}

function Portfolio({ s, L, dl }) {
  const d = useData(s, "/dashboard/portfolio");
  if (!d) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  const c = d.cards;
  const chart = d.by_status.filter((x) => x.count > 0).map((x) => ({ name: x.label?.ar || x.status, count: x.count }));
  return (
    <div className="space-y-4" data-testid="cc-portfolio">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Kpi tid="clients" label={L("العملاء", "Clients")} value={c.clients} />
        <Kpi tid="engagements" label={L("الارتباطات", "Engagements")} value={c.engagements} />
        <Kpi tid="periods-active" label={L("الفترات النشطة", "Active periods")} value={c.periods_active} />
        <Kpi tid="filed-returns" label={L("الإقرارات المُقدَّمة", "Filed")} value={c.filed_returns} />
        <Kpi tid="overdue-alerts" label={L("تنبيهات متجاوزة", "Overdue")} value={c.overdue_alerts} tone={c.overdue_alerts ? "bg-[hsl(354_60%_45%)]/8" : ""} />
        <Kpi tid="vat-payable" label={L("المستحق", "Payable")} value={c.vat_payable} />
        <Kpi tid="vat-credit" label={L("الرصيد الدائن", "Credit")} value={c.vat_credit} />
        <Kpi tid="refunds-in-progress" label={L("الاسترداد الجاري", "Refunds")} value={c.refunds_in_progress} />
        <Kpi tid="zatca-open" label={L("ملفات الهيئة", "ZATCA open")} value={c.zatca_open} />
      </div>
      <Card>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase">{L("الفترات حسب الحالة", "Periods by status")}</div>
          <button data-testid="cc-export-portfolio" onClick={() => dl("portfolio")} className="inline-flex items-center gap-1 text-[11px] rounded border border-border px-2 py-1 hover:bg-secondary"><Download className="h-3 w-3" />CSV</button>
        </div>
        {chart.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chart} layout="vertical" margin={{ left: 40 }}>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
              <Tooltip /><Bar dataKey="count" fill={NAVY} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="text-[11px] text-muted-foreground">{L("لا بيانات", "No data")}</div>}
      </Card>
    </div>
  );
}

function Deadlines({ s, L, dl }) {
  const d = useData(s, "/dashboard/deadlines");
  if (!d) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  const color = (st) => st === "OVERDUE" ? "bg-[hsl(354_60%_45%)]/12 text-[hsl(354_60%_40%)]" : st === "DUE_SOON" ? "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]" : "bg-secondary text-muted-foreground";
  return (
    <Card data-testid="cc-deadlines">
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-2 text-[11px]">
          <span className="rounded px-2 py-1 bg-[hsl(354_60%_45%)]/12 text-[hsl(354_60%_40%)]">{L("متجاوز", "Overdue")}: {d.summary.overdue}</span>
          <span className="rounded px-2 py-1 bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]">{L("وشيك", "Due soon")}: {d.summary.due_soon}</span>
          <span className="rounded px-2 py-1 bg-secondary">{L("مفتوح", "Open")}: {d.summary.open}</span>
        </div>
        <button data-testid="cc-export-deadlines" onClick={() => dl("deadlines")} className="inline-flex items-center gap-1 text-[11px] rounded border border-border px-2 py-1 hover:bg-secondary"><Download className="h-3 w-3" />CSV</button>
      </div>
      <div className="space-y-1" data-testid="cc-deadline-list">
        {d.items.map((i, n) => (
          <div key={n} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5">
            <span className={`px-1.5 py-0.5 rounded ${color(i.status)}`}>{i.status || "—"}</span>
            <span className="bg-secondary px-1.5 py-0.5 rounded">{i.type}</span>
            <span className="flex-1 truncate">{i.title}</span>
            <span className="text-muted-foreground">{i.due_date || "—"}</span>
            <span className="text-muted-foreground text-[10px]">{L("مصدر", "src")}: {i.source}</span>
          </div>
        ))}
        {!d.items.length && <div className="text-[11px] text-muted-foreground">{L("لا مواعيد", "No deadlines")}</div>}
      </div>
    </Card>
  );
}

function Pipeline({ s, L, dl }) {
  const d = useData(s, "/dashboard/pipeline");
  if (!d) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  return (
    <div className="space-y-3" data-testid="cc-pipeline">
      <Card>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase">{L("المتعثّر (>14 يوماً)", "Stuck (>14 days)")}: {d.stuck_count}</div>
          <button data-testid="cc-export-pipeline" onClick={() => dl("pipeline")} className="inline-flex items-center gap-1 text-[11px] rounded border border-border px-2 py-1 hover:bg-secondary"><Download className="h-3 w-3" />CSV</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="cc-pipeline-cols">
          {d.columns.filter((c) => c.count > 0).map((c) => (
            <div key={c.status} className="rounded-lg border border-border p-2">
              <div className="text-[10px] text-muted-foreground mb-1">{c.label?.ar || c.status}</div>
              <div className="text-lg font-bold">{c.count}</div>
              {c.items.filter((i) => i.stuck).length > 0 && <div className="text-[9px] text-[hsl(354_60%_40%)] mt-0.5">{c.items.filter((i) => i.stuck).length} {L("متعثّر", "stuck")}</div>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Filing({ s, L }) {
  const d = useData(s, "/dashboard/filing-payment");
  if (!d) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  const f = d.refund_funnel; const cp = d.credit_position;
  return (
    <div className="space-y-4" data-testid="cc-filing">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi tid="payable-total" label={L("المستحق", "Payable")} value={d.payable_total} />
        <Kpi tid="confirmed-paid" label={L("مُسدَّد مُعتمد", "Confirmed paid")} value={d.confirmed_paid} />
        <Kpi tid="outstanding" label={L("المتبقي", "Outstanding")} value={d.outstanding} tone={d.outstanding > 0 ? "bg-[hsl(41_72%_50%)]/8" : ""} />
        <Kpi tid="pending-payment" label={L("بانتظار اعتماد", "Pending")} value={d.pending_payment} />
      </div>
      <Card>
        <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("قمع الاسترداد", "Refund funnel")}</div>
        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <Kpi tid="refund-requested" label={L("مطلوب", "Requested")} value={f.requested} /><Kpi tid="refund-approved" label={L("معتمد", "Approved")} value={f.approved} /><Kpi tid="refund-paid" label={L("مدفوع", "Paid")} value={f.paid} />
        </div>
      </Card>
      <Card>
        <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("مركز الرصيد الدائن", "Credit position")}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]" data-testid="cc-credit">
          <Kpi tid="credit-generated" label={L("مُولّد", "Generated")} value={cp.credit_generated} /><Kpi tid="credit-utilized" label={L("مُستخدم", "Utilized")} value={cp.credit_utilized} />
          <Kpi tid="credit-closing" label={L("رصيد ختامي", "Closing")} value={cp.closing_credit} /><Kpi tid="credit-eligible" label={L("قابل للاسترداد", "Eligible")} value={cp.refund_eligible_credit} />
        </div>
      </Card>
    </div>
  );
}

function Exceptions({ s, L }) {
  const d = useData(s, "/dashboard/exceptions");
  if (!d) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  const ce = d.compliance_exceptions; const re = d.reconciliation_exceptions;
  const Risk = ({ r }) => (<span className="flex gap-1">
    <span className="rounded px-1.5 bg-[hsl(354_60%_45%)]/12 text-[hsl(354_60%_40%)]">RED {r.RED}</span>
    <span className="rounded px-1.5 bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]">AMBER {r.AMBER}</span></span>);
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="cc-exceptions">
      <Card><div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("استثناءات الامتثال المفتوحة", "Open compliance exceptions")}: {ce.open}</div><Risk r={ce.by_risk} /></Card>
      <Card><div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("استثناءات التسوية المفتوحة", "Open reconciliation exceptions")}: {re.open}</div><Risk r={re.by_risk} /></Card>
      <Card><div className="text-xs font-semibold text-muted-foreground uppercase mb-1">{L("ملاحظات أوراق العمل المفتوحة", "Open WP notes")}</div><div className="text-lg font-bold">{d.wp_open_notes}</div></Card>
      <Card data-testid="cc-blockers"><div className="text-xs font-semibold text-muted-foreground uppercase mb-1">{L("موانع الإغلاق (مراجعة بعدية)", "Closure blockers")}</div><div className="text-lg font-bold">{d.closure_blockers}</div></Card>
    </div>
  );
}

function Zatca({ s, L, dl }) {
  const d = useData(s, "/dashboard/zatca");
  if (!d) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  return (
    <div className="space-y-3" data-testid="cc-zatca">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi tid="zatca-open-count" label={L("مفتوحة", "Open")} value={d.open} />
        <Kpi tid="zatca-handoffs" label={L("تسليم اعتراض", "Handoffs")} value={d.handoffs} />
        <Kpi tid="zatca-variance-total" label={L("إجمالي الفروقات", "Variance total")} value={d.assessment_variance_total} />
        <Kpi tid="zatca-total" label={L("الإجمالي", "Total")} value={d.items.length} />
      </div>
      <Card>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase">{L("الملفات", "Cases")}</div>
          <button data-testid="cc-export-zatca" onClick={() => dl("zatca")} className="inline-flex items-center gap-1 text-[11px] rounded border border-border px-2 py-1 hover:bg-secondary"><Download className="h-3 w-3" />CSV</button>
        </div>
        <div className="space-y-1" data-testid="cc-zatca-list">
          {d.items.map((i) => (
            <div key={i.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5">
              <span className="font-mono">{i.id}</span><span className="bg-secondary px-1.5 py-0.5 rounded">{i.type}</span>
              <span className="flex-1">{i.status}</span><span className="text-muted-foreground">{i.deadline || "—"}</span>
              {i.handoff && <span className="text-[9px] text-[hsl(354_60%_40%)]">{L("تصعيد", "handoff")}</span>}
            </div>
          ))}
          {!d.items.length && <div className="text-[11px] text-muted-foreground">{L("لا ملفات", "No cases")}</div>}
        </div>
      </Card>
    </div>
  );
}

function Workload({ s, L, dl }) {
  const d = useData(s, "/dashboard/workload");
  if (!d) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  const q = d.queue_depth;
  return (
    <div className="space-y-3" data-testid="cc-workload">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Kpi tid="queue-preparer" label={L("طابور المُعدّ", "Preparer queue")} value={q.preparer} />
        <Kpi tid="queue-reviewer" label={L("طابور المراجع", "Reviewer queue")} value={q.reviewer} />
        <Kpi tid="queue-manager" label={L("طابور المدير", "Manager queue")} value={q.tax_manager} />
        <Kpi tid="queue-filer" label={L("طابور المُقدِّم", "Filer queue")} value={q.authorized_filer} />
        <Kpi tid="overdue-periods" label={L("فترات متجاوزة", "Overdue periods")} value={d.overdue_periods} tone={d.overdue_periods ? "bg-[hsl(354_60%_45%)]/8" : ""} />
      </div>
      <Card>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase">{L("العبء حسب المُكلَّف (مراقبة فقط)", "Workload by owner (monitoring only)")}</div>
          <button data-testid="cc-export-workload" onClick={() => dl("workload")} className="inline-flex items-center gap-1 text-[11px] rounded border border-border px-2 py-1 hover:bg-secondary"><Download className="h-3 w-3" />CSV</button>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-[11px]" data-testid="cc-workload-table"><thead><tr className="text-right text-muted-foreground">
          <th className="py-1 pe-3">{L("الدور", "Slot")}</th><th className="pe-3">{L("المُكلَّف", "Owner")}</th><th className="pe-3">{L("نشط", "Active")}</th><th className="pe-3">{L("في الطابور", "In queue")}</th><th className="pe-3">{L("متجاوز", "Overdue")}</th><th>{L("أقدم تقادم", "Max aging")}</th></tr></thead>
          <tbody>{d.rows.map((r, n) => (<tr key={n} className="border-t border-border"><td className="py-1 pe-3">{r.slot}</td><td className="pe-3">{r.owner}</td><td className="pe-3">{r.active}</td><td className="pe-3">{r.in_queue}</td><td className="pe-3">{r.overdue}</td><td>{r.max_aging}d</td></tr>))}</tbody>
        </table></div>
        {!d.rows.length && <div className="text-[11px] text-muted-foreground">{L("لا بيانات عبء", "No workload data")}</div>}
      </Card>
    </div>
  );
}
